'use client';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import Link from 'next/link';
import React, { FC, useMemo } from 'react';

import { AbgTypography, ArrowRightIcon } from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { bookingFlowStoreSelector } from '../../store';
import { AemImage } from '..';

import {
  buttonStyles,
  buttonTextStyles,
  containerStyles,
  contentContainerStyles,
  contentWrapper,
  iconStyles,
  imageStyles,
  subtitleStyles,
} from './partner-benefits-banner.styling';
import { PartnerBenefitsBannerProperties } from './partner-benefits-banner.types';
import { extractCTALink } from './utils';

export const PartnerBenefitsBanner: FC<PartnerBenefitsBannerProperties> = ({
  model: { header, subheader, bannerImage, codeList, button },
  aemAttributes,
}) => {
  const awdCode = bookingFlowStoreSelector.use.awdCode?.();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { config } = usePageContext();

  const isAuthorMode = useMemo(
    () => config.app?.runmode === 'author',
    [config.app?.runmode],
  );

  if (
    !isAuthorMode &&
    (!awdCode || !codeList?.some((code) => code.awdCode === awdCode))
  ) {
    return (
      <div
        {...aemAttributes}
        data-aue-type="component"
        data-testid="partner-benefits-banner"
      />
    );
  }

  const ctaLink = button ? extractCTALink(button) : null;

  return (
    <Box
      {...aemAttributes}
      data-aue-type="component"
      sx={containerStyles}
      data-testid="partner-benefits-banner"
    >
      <Box sx={contentWrapper}>
        {bannerImage && (
          <Box sx={imageStyles}>
            <AemImage
              src={bannerImage.source}
              alt={bannerImage?.description || ''}
              fill
              data-testid="partner-benefits-banner-image"
            />
          </Box>
        )}
        <Box sx={contentContainerStyles}>
          <AbgTypography variant="h6">{header}</AbgTypography>
          {subheader && (
            <AbgTypography
              variant={isMobile ? 'bodySmallMedium' : 'bodyRegularMedium'}
              sx={subtitleStyles}
              dangerouslySetInnerHTML={{ __html: subheader }}
            />
          )}
        </Box>
      </Box>

      {button && (
        <Box
          sx={buttonStyles}
          component={ctaLink?.href ? Link : 'div'}
          {...(ctaLink?.href
            ? {
                href: ctaLink.href,
                target: ctaLink.target,
                title: ctaLink.title,
              }
            : {})}
        >
          <AbgTypography
            sx={buttonTextStyles}
            dangerouslySetInnerHTML={{ __html: button }}
          />
          {ctaLink?.href && <ArrowRightIcon sx={iconStyles} />}
        </Box>
      )}
    </Box>
  );
};
